import { Button } from 'components/Button/Button';
import { FormikTypeaheadInput } from 'components/FormikTypeaheadInput/FormikTypeaheadInput';
import { TypeaheadDropdownContent } from 'components/FormikTypeaheadInput/TypeaheadDropdownContent';
import { useApiTableAutocomplete } from 'components/FormikTypeaheadInput/useApiTableAutocomplete';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import type { FC } from 'react';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';

interface Props {
  onClose: () => void;
  onConfirm: (newSiteName: string) => void;
}

export interface AddToSiteFormValues {
  siteName: string;
}

export const AddToSiteDialog: FC<Props> = (props) => {
  const {
    manualSeparation: { addToSiteDialog: addToSiteDialogMsg },
  } = useLocale();

  const siteNameAutocomplete = useApiTableAutocomplete<{ siteName: string }>({
    snapshot: true,
    createRequest: (searchEvent) => ({
      url: '/tables/inventory/sites',
      data: {
        columns: ['siteName'],
        filters: {
          or: [{ siteName: ['like', searchEvent.inputValue] }],
        },
      },
    }),
  });

  const handleSubmit = (values: AddToSiteFormValues) => {
    props.onConfirm(values.siteName);
  };

  const formik = useFormik({
    initialValues: {
      siteName: '',
    },
    validate: (
      values: AddToSiteFormValues,
    ): FormikErrors<AddToSiteFormValues> => {
      const errors: FormikErrors<AddToSiteFormValues> = {};

      if (values.siteName === '') {
        errors.siteName = addToSiteDialogMsg.required;
      }

      return errors;
    },
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  return (
    <OverlayModal
      onClose={props.onClose}
      contentClassName='manual-site-separation__overlay-content flex flex-col'
    >
      <form onSubmit={formik.handleSubmit}>
        <OverlayModalHeader>
          <h4>{addToSiteDialogMsg.title}</h4>
        </OverlayModalHeader>
        <OverlayModalBody>
          <div className='relative'>
            <FormikTypeaheadInput
              label={addToSiteDialogMsg.siteName}
              autoTrim='left'
              formik={formik}
              inputClassName='ipf-input'
              loading={siteNameAutocomplete.loading}
              menuContent={
                <TypeaheadDropdownContent
                  options={siteNameAutocomplete.content}
                  onSelect={(option) => {
                    formik.setFieldValue('siteName', option.siteName);
                  }}
                  renderOption={({ siteName }) => siteName}
                />
              }
              name='siteName'
              onFocus={siteNameAutocomplete.search}
              onSearch={siteNameAutocomplete.search}
            />
          </div>
        </OverlayModalBody>
        <OverlayModalFooter>
          <div className='flex justify-between'>
            <Button
              variant='primary'
              onClick={() => {
                formik.submitForm();
              }}
              disabled={!formik.isValid}
              type='button'
            >
              {addToSiteDialogMsg.btnChange}
            </Button>
            <Button variant='secondary' type='button' onClick={props.onClose}>
              {addToSiteDialogMsg.btnClose}
            </Button>
          </div>
        </OverlayModalFooter>
      </form>
    </OverlayModal>
  );
};
